/**
 * PRINT PREVIEW PAGE
 * Shows recent print jobs from Firestore rendered as full invoice previews.
 * Matches the exact invoice format used by "Print Invoice" button.
 */
import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../../services/supabaseClient';
import { buildOrderZatcaPayload, type SellerProfile } from '../../services/zatcaQr';

interface PrintJobData {
  id: string;
  order_number: string;
  type: 'cashier-receipt' | 'kitchen-order';
  printer_name: string;
  printer_ip: string;
  status: string;
  created_at: any;
  printed_at: any;
  error_message: string | null;
  order_data: any;
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  printing: '#3b82f6',
  printed: '#22c55e',
  failed: '#ef4444',
};

export default function PrintPreviewPage() {
  const [jobs, setJobs] = useState<PrintJobData[]>([]);
  const [selectedJob, setSelectedJob] = useState<PrintJobData | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'cashier-receipt' | 'kitchen-order'>('all');
  const [seller, setSeller] = useState<SellerProfile | null>(null);

  useEffect(() => {
    const loadJobs = async () => {
      const { data } = await supabase
        .from('print_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      const list = (data ?? []).map((row: any) => ({
        id: row.id,
        order_number: row.order_number,
        type: row.type,
        printer_name: row.printer_name,
        printer_ip: row.printer_ip,
        status: row.status,
        created_at: row.created_at,
        printed_at: row.printed_at,
        error_message: row.error_message,
        order_data: row.order_data,
      })) as PrintJobData[];
      setJobs(list);
      setSelectedJob((prev) => prev ?? list[0] ?? null);
    };

    void loadJobs();

    // Subscribe to live updates so newly-printed jobs appear without refresh.
    const channel = supabase
      .channel('print_jobs_preview')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'print_jobs' }, () => { void loadJobs(); })
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load seller info once so the QR can be rendered on the right-side preview.
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data: session } = await supabase.auth.getSession();
      const tenantId = (session.session?.user?.app_metadata as any)?.tenant_id as string | undefined;
      if (!tenantId) return;
      const { data } = await supabase.rpc('public_tenant_info', { p_tenant_id: tenantId });
      const t = Array.isArray(data) ? data[0] : data;
      if (alive && t) {
        setSeller({
          name: t.legal_name || t.display_name || 'Saheeh',
          vatNumber: t.vat_number || '',
          region: t.region || null,
        });
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = filterType === 'all' ? jobs : jobs.filter((j) => j.type === filterType);

  const formatTime = (ts: any) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div style={{ display: 'flex', height: '100%', background: '#1a1a2e', color: '#e0e0e0', fontFamily: 'system-ui, sans-serif' }}>
      {/* LEFT: Job List */}
      <div style={{ width: 360, borderRight: '1px solid #333', overflow: 'auto', flexShrink: 0 }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #333' }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#fff' }}>Print Preview</h2>
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {(['all', 'cashier-receipt', 'kitchen-order'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilterType(f)}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
                  background: filterType === f ? '#6366f1' : '#2d2d44', color: '#fff',
                }}
              >
                {f === 'all' ? 'All' : f === 'cashier-receipt' ? 'Cashier' : 'Kitchen'}
              </button>
            ))}
          </div>
        </div>
        {filtered.map((job) => (
          <div
            key={job.id}
            onClick={() => setSelectedJob(job)}
            style={{
              padding: '12px 16px', borderBottom: '1px solid #2a2a3e', cursor: 'pointer',
              background: selectedJob?.id === job.id ? '#2d2d55' : 'transparent',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, color: '#fff' }}>#{job.order_number}</span>
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 10,
                background: STATUS_COLORS[job.status] || '#666', color: '#fff',
              }}>
                {job.status}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
              {job.type === 'cashier-receipt' ? 'Cashier' : 'Kitchen'} — {job.printer_name}
            </div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{formatTime(job.created_at)}</div>
            {job.error_message && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{job.error_message}</div>}
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>No print jobs found</div>
        )}
      </div>

      {/* RIGHT: Invoice Preview */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '24px 16px' }}>
        {selectedJob ? (
          <InvoicePreview job={selectedJob} seller={seller} />
        ) : (
          <div style={{ color: '#666', alignSelf: 'center' }}>Select a print job</div>
        )}
      </div>
    </div>
  );
}

/* ─── Full Invoice Preview Component ─── */
function InvoicePreview({ job, seller }: { job: PrintJobData; seller: SellerProfile | null }) {
  const d = job.order_data || {};
  const isKitchen = job.type === 'kitchen-order';
  const orderType = d.order_type || d.orderType || 'dine_in';
  const tableNum = d.tableNumber || d.table_number || '';
  const custName = d.customerName || d.customer_name || '';
  const custAddress = d.customerAddress || d.customer_address || d.delivery_address || '';
  const custPhone = d.customerPhone || d.customer_phone || '';
  const isDefaultName = !custName || custName === 'Laufkunde' || custName === 'Walk-in Customer' || custName.startsWith('Dine-In');
  const items = d.items || [];
  const orderNum = d.orderNumber || d.order_number || job.order_number || '';
  const total = d.total || 0;
  const subtotal = d.subtotal || 0;
  const tax = d.tax || 0;
  const paymentMethod = d.paymentMethod || d.payment_method || 'cash';
  const paymentLabel = paymentMethod === 'cash' ? 'Cash'
    : paymentMethod === 'card' ? 'Card'
    : paymentMethod === 'mada' ? 'Mada'
    : paymentMethod === 'apple_pay' ? 'Apple Pay'
    : paymentMethod === 'stc_pay' ? 'STC Pay'
    : paymentMethod === 'paypal' ? 'PayPal'
    : paymentMethod;
  const orderDate = d.orderDate || '';
  const orderTime = d.orderTime || '';
  const currency = seller?.region === 'SA' ? 'SAR' : 'EUR';
  const money = (v: number) => currency === 'SAR' ? `${(Number(v) || 0).toFixed(2)} SAR` : `€${(Number(v) || 0).toFixed(2)}`;
  const vatRate = subtotal > 0 ? Math.round((tax / subtotal) * 100) : 15;
  const orderTypeLabel = orderType === 'delivery' ? 'Delivery'
    : orderType === 'takeaway' ? 'Takeaway'
    : orderType === 'pickup' ? 'Pickup'
    : 'Dine-in';

  const invoiceStyle: React.CSSProperties = {
    width: 420,
    background: '#fff',
    color: '#000',
    fontFamily: 'Arial, sans-serif',
    fontSize: 13,
    padding: '24px 20px',
    borderRadius: 4,
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
    lineHeight: 1.6,
  };

  if (isKitchen) {
    return <KitchenPreview job={job} />;
  }

  return (
    <div style={invoiceStyle}>
      {/* Header */}
      <div style={{ textAlign: 'center', borderBottom: '2px solid #0A2540', paddingBottom: 15, marginBottom: 16 }}>
        <img
          src="/saheeh_logo_w.svg"
          alt="Saheeh"
          style={{ maxWidth: 130, marginBottom: 8, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
        />
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: 1, color: '#0A2540' }}>
          {seller?.name || 'Saheeh POS'}
        </p>
        {seller?.region === 'SA' && seller?.vatNumber && (
          <p style={{ margin: '4px 0 0 0', fontSize: 11, color: '#0A2540', fontWeight: 600 }}>
            VAT No.: {seller.vatNumber}
          </p>
        )}
      </div>

      {/* Order meta */}
      <div style={{ marginBottom: 16, fontSize: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
          <span><strong>Invoice #</strong></span><span>{orderNum}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
          <span><strong>Date</strong></span><span>{orderDate} {orderTime}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
          <span><strong>Type</strong></span><span>{orderTypeLabel}</span>
        </div>
        {tableNum && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
            <span><strong>Table</strong></span><span>{tableNum}</span>
          </div>
        )}
      </div>

      {/* Customer */}
      <div style={{ marginBottom: 16, padding: 10, background: '#f7faf9', borderRadius: 6, border: '1px solid #e2e8f0' }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 700, fontSize: 18, color: '#14B8A6' }}>#{orderNum}</p>
        {!isDefaultName && custName && (
          <p style={{ margin: '4px 0', fontWeight: 600, fontSize: 13 }}>{custName}</p>
        )}
        {custAddress && <p style={{ margin: '4px 0', fontSize: 12, color: '#475569' }}>{custAddress}</p>}
        {custPhone && <p style={{ margin: '4px 0', fontSize: 12, color: '#475569' }}>Tel.: {custPhone}</p>}
      </div>

      {/* Items */}
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 10px 0', color: '#0A2540', borderBottom: '1px solid #cbd5e1', paddingBottom: 4, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase' }}>
          Items
        </h3>
        {items.map((item: any, i: number) => {
          const qty = item.quantity || 1;
          const price = item.total || (item.price || 0) * qty;
          const name = item.name || item.menu_item_name || 'Item';
          return (
            <div key={i} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px dotted #cbd5e1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700 }}>{qty}× {name}</span>
                <span style={{ fontWeight: 700 }}>{money(price)}</span>
              </div>
              {item.addons?.map((addon: any, j: number) => (
                <div key={j} style={{ paddingLeft: 16, color: '#64748B', fontSize: '0.9em' }}>
                  + {typeof addon === 'string' ? addon : addon.name || ''}
                  {addon.price ? ` ${money((addon.price || 0) * (addon.quantity || 1))}` : ''}
                </div>
              ))}
              {item.notes && <div style={{ paddingLeft: 16, color: '#475569', fontStyle: 'italic', fontSize: 12 }}>Note: {item.notes}</div>}
            </div>
          );
        })}
      </div>

      {/* Totals */}
      <div style={{ borderTop: '2px solid #0A2540', paddingTop: 12, marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span>Subtotal (excl. VAT)</span>
          <span>{money(subtotal)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span>VAT {vatRate}%</span>
          <span>{money(tax)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 17, color: '#0A2540', borderTop: '1px solid #0A2540', paddingTop: 8, marginTop: 4 }}>
          <span>TOTAL</span>
          <span>{money(total)}</span>
        </div>
      </div>

      {/* Payment */}
      <div style={{ textAlign: 'center', margin: '16px 0', padding: 12, background: '#ecfdf5', borderRadius: 6 }}>
        <div style={{ fontSize: 11, color: '#047857', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>
          Payment
        </div>
        <p style={{ margin: '4px 0', fontWeight: 700, fontSize: 14, color: '#065f46' }}>
          {paymentLabel} · {money(total)}
        </p>
        <p style={{ margin: '4px 0', fontSize: 11, color: '#047857', fontWeight: 600 }}>PAID</p>
      </div>

      {/* ZATCA QR */}
      {(() => {
        const payload = buildOrderZatcaPayload({
          seller,
          taxMeta: d.tax_meta || null,
          grandTotal: total,
          taxTotal: Number(d.tax ?? d.tax_total ?? 0),
          createdAt: job.created_at,
        });
        if (!payload) return null;
        return (
          <div style={{ textAlign: 'center', margin: '16px 0', padding: 14, border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
              <QRCodeSVG value={payload} size={160} level="M" bgColor="#ffffff" fgColor="#0A2540" />
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#0A2540', letterSpacing: 1 }}>
              ZATCA · Scan to verify
            </div>
            <div style={{ fontSize: 10, color: '#64748B', marginTop: 2 }}>
              Use the ZATCA app to validate this invoice.
            </div>
          </div>
        );
      })()}

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px dashed #cbd5e1', fontSize: 11, color: '#64748B' }}>
        <p style={{ margin: '4px 0', color: '#0A2540', fontWeight: 600 }}>Thank you for choosing {seller?.name || 'Saheeh'}</p>
        <p style={{ margin: '4px 0' }}>www.saheehpos.com</p>
      </div>

      {/* Print job info */}
      <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 10, marginTop: 12, paddingTop: 6, borderTop: '1px dashed #e2e8f0' }}>
        Printer: {job.printer_name} ({job.printer_ip}) · Status: {job.status}
      </div>
    </div>
  );
}

/* ─── Kitchen Ticket Preview ─── */
function KitchenPreview({ job }: { job: PrintJobData }) {
  const d = job.order_data || {};
  const orderType = d.order_type || d.orderType || 'dine_in';
  const tableNum = d.tableNumber || d.table_number || '';
  const custName = d.customerName || d.customer_name || '';
  const isDefaultName = !custName || custName === 'Laufkunde' || custName === 'Walk-in Customer' || custName.startsWith('Dine-In');
  const items = d.items || [];
  const orderNum = d.orderNumber || d.order_number || job.order_number || '';

  const receiptStyle: React.CSSProperties = {
    width: 320,
    background: '#fff',
    color: '#000',
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: 13,
    padding: '20px 16px',
    borderRadius: 4,
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
    lineHeight: 1.5,
  };

  const center: React.CSSProperties = { textAlign: 'center' };
  const bold: React.CSSProperties = { fontWeight: 'bold' };
  const divider = '————————————————————————————';
  const bigText: React.CSSProperties = { fontSize: 20, fontWeight: 'bold' };
  const orderTypeLabel = orderType === 'delivery' ? 'LIEFERUNG' : orderType === 'takeaway' ? 'ABHOLUNG' : 'IM RESTAURANT';

  return (
    <div style={receiptStyle}>
      <div style={center}><strong style={{ fontSize: 16 }}>KITCHEN TICKET</strong></div>
      <div style={{ ...center, margin: '8px 0' }}>{divider}</div>

      {orderType === 'dine_in' ? (
        <>
          <div style={{ ...center, ...bigText, margin: '8px 0' }}>
            {tableNum ? `Table ${tableNum}` : 'Dine-In'}
          </div>
          {!isDefaultName && <div style={{ ...center, ...bold }}>{custName}</div>}
        </>
      ) : (
        <>
          <div style={{ ...center, ...bigText, margin: '8px 0' }}>#{orderNum}</div>
          {!isDefaultName && <div style={{ ...center, ...bold }}>{custName}</div>}
          {d.customerAddress && <div style={center}>{d.customerAddress}</div>}
          {d.customerPhone && <div style={center}>Tel.: {d.customerPhone}</div>}
        </>
      )}

      <div style={{ margin: '6px 0' }}>{divider}</div>
      <div style={center}>
        <div style={bold}>Bestell-Nr.: {orderNum}</div>
        <div style={bold}>{orderTypeLabel}</div>
        {d.orderDate && <div>Datum: {d.orderDate} | {d.orderTime} Uhr</div>}
        <div style={bold}>SOFORT</div>
      </div>

      <div style={{ margin: '6px 0' }}>{divider}</div>
      <div style={{ ...center, ...bold }}>ARTIKEL:</div>
      <div>{divider}</div>

      {groupByCategory(items).map(([cat, catItems]: [string, any[]]) => (
        <div key={cat}>
          <div style={{ ...bold, marginTop: 6 }}>{cat.toUpperCase()}</div>
          {catItems.map((item: any, i: number) => (
            <div key={i}>
              <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                {item.quantity || 1} x {item.name || item.menu_item_name}
              </div>
              {(item.notes || item.special_instructions) && (
                <div style={{ color: '#666' }}>&nbsp;&nbsp;&gt;&gt; {item.notes || item.special_instructions}</div>
              )}
              {item.addons?.map((a: any, j: number) => (
                <div key={j} style={{ color: '#555' }}>&nbsp;&nbsp;+ {typeof a === 'string' ? a : a.name_de || a.name}</div>
              ))}
            </div>
          ))}
        </div>
      ))}

      <div style={{ margin: '8px 0' }}>{divider}</div>
      <div style={{ ...center, ...bold, marginTop: 8 }}>KÜCHENBON</div>

      <div style={{ textAlign: 'center', color: '#999', fontSize: 10, marginTop: 12 }}>
        Drucker: {job.printer_name} ({job.printer_ip}) | Status: {job.status}
      </div>
    </div>
  );
}

/* Helper: group items by category */
function groupByCategory(items: any[]): [string, any[]][] {
  const map: Record<string, any[]> = {};
  for (const item of items) {
    const cat = item.category_name || item.category || 'Sonstiges';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  }
  return Object.entries(map);
}
